import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function HangmanRankings({ user }) {
  const { hangmanId } = useParams();
  const navigate = useNavigate();
  const [rankings, setRankings] = useState([]);
  const [hangman, setHangman] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, [hangmanId]);

  const loadData = async () => {
    try {
      const [rankingsRes, hangmanRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/hangman/${hangmanId}/rankings`),
        axios.get(`${BACKEND_URL}/api/hangman/${hangmanId}`)
      ]);
      
      setRankings(rankingsRes.data.rankings);
      setHangman(hangmanRes.data);
    } catch (error) {
      console.error('Error loading rankings:', error);
      alert('Error al cargar el ranking');
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getMedalEmoji = (position) => {
    switch(position) {
      case 0: return '🥇';
      case 1: return '🥈';
      case 2: return '🥉';
      default: return `${position + 1}º`;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-50 to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-pink-600"></div>
          <p className="mt-4 text-gray-600">Cargando ranking...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 to-purple-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div>
              <button
                onClick={() => navigate('/dashboard')}
                className="text-pink-600 hover:text-pink-700 mb-2 flex items-center gap-2"
              >
                ← Volver al Dashboard
              </button>
              <h1 className="text-2xl font-bold text-gray-800">🏆 Clasificación - {hangman?.title}</h1>
              <p className="text-sm text-gray-600 mt-1">Top jugadores ordenados por puntuación ponderada</p>
            </div>
          </div>
        </div>
      </header>

      {/* Rankings */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Explanation Card */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <h3 className="text-lg font-bold text-gray-800 mb-3">📊 Cómo se calcula la puntuación</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
            <div className="text-center p-3 bg-blue-50 rounded-lg">
              <div className="text-2xl mb-2">🎯</div>
              <div className="font-bold text-blue-700">40%</div>
              <div className="text-gray-600">Puntuación</div>
            </div>
            <div className="text-center p-3 bg-green-50 rounded-lg">
              <div className="text-2xl mb-2">⚡</div>
              <div className="font-bold text-green-700">20%</div>
              <div className="text-gray-600">Rapidez</div>
            </div>
            <div className="text-center p-3 bg-yellow-50 rounded-lg">
              <div className="text-2xl mb-2">🎲</div>
              <div className="font-bold text-yellow-700">20%</div>
              <div className="text-gray-600">Intentos</div>
            </div>
            <div className="text-center p-3 bg-red-50 rounded-lg">
              <div className="text-2xl mb-2">💗</div>
              <div className="font-bold text-red-700">20%</div>
              <div className="text-gray-600">Vidas</div>
            </div>
          </div>
        </div>

        {rankings.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl">
            <div className="text-6xl mb-4">🎮</div>
            <h3 className="text-xl font-semibold text-gray-700 mb-2">
              Aún no hay resultados
            </h3>
            <p className="text-gray-500">Sé el primero en jugar y aparecer en el ranking</p>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gradient-to-r from-pink-600 to-purple-600 text-white">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider">
                      Posición
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider">
                      Jugador
                    </th>
                    <th className="px-6 py-4 text-center text-xs font-bold uppercase tracking-wider">
                      Puntuación<br/>Ponderada
                    </th>
                    <th className="px-6 py-4 text-center text-xs font-bold uppercase tracking-wider">
                      Puntos
                    </th>
                    <th className="px-6 py-4 text-center text-xs font-bold uppercase tracking-wider">
                      Tiempo
                    </th>
                    <th className="px-6 py-4 text-center text-xs font-bold uppercase tracking-wider">
                      Intentos
                    </th>
                    <th className="px-6 py-4 text-center text-xs font-bold uppercase tracking-wider">
                      Vidas<br/>Perdidas
                    </th>
                    <th className="px-6 py-4 text-center text-xs font-bold uppercase tracking-wider">
                      Palabras
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {rankings.map((ranking, index) => (
                    <tr 
                      key={index} 
                      className={`hover:bg-gray-50 ${
                        ranking.user_email === user?.email ? 'bg-yellow-50' : ''
                      }`}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className={`text-2xl font-bold ${
                          index < 3 ? 'text-3xl' : 'text-gray-700'
                        }`}>
                          {getMedalEmoji(index)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {ranking.user_name}
                              {ranking.user_email === user?.email && (
                                <span className="ml-2 px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded-full">
                                  Tú
                                </span>
                              )}
                            </div>
                            <div className="text-sm text-gray-500">{ranking.user_email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <div className="text-2xl font-bold text-pink-600">
                          {ranking.ranking_score.toFixed(1)}
                        </div>
                        <div className="text-xs text-gray-500">sobre 100</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <div className="text-lg font-semibold text-blue-600">
                          {ranking.score}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <div className="text-sm font-medium text-gray-900">
                          {formatTime(ranking.total_time_seconds)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <div className="text-sm font-medium text-gray-900">
                          {ranking.total_attempts}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <div className="flex items-center justify-center gap-1">
                          <span className="text-sm font-medium text-gray-900">
                            {ranking.total_lives_lost}
                          </span>
                          <span className="text-red-500">💔</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <div className="text-sm text-gray-900">
                          {ranking.words_completed}/{ranking.words_attempted}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Legend */}
        <div className="mt-6 bg-white rounded-xl shadow-sm p-6">
          <h3 className="text-lg font-bold text-gray-800 mb-3">🎯 Leyenda</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600">
            <div>
              <strong>Puntuación Ponderada:</strong> Calificación global que considera todos los factores
            </div>
            <div>
              <strong>Puntos:</strong> Puntuación bruta obtenida en el juego
            </div>
            <div>
              <strong>Tiempo:</strong> Duración total del juego (menos es mejor)
            </div>
            <div>
              <strong>Intentos:</strong> Letras totales probadas (menos es mejor)
            </div>
            <div>
              <strong>Vidas Perdidas:</strong> Errores cometidos (menos es mejor)
            </div>
            <div>
              <strong>Palabras:</strong> Palabras completadas / Total de palabras
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default HangmanRankings;
